/**
 * EditableField — a labelled field that starts LOCKED (read-only) and unlocks
 * one field at a time via a pencil, mirroring the "Edit Food Item" pattern. Used
 * when creating a new recipe version: the previous version's values are shown,
 * and the clinician deliberately unlocks only the fields they want to change.
 *
 * The parent owns the field state and computes `disabled` for the control; this
 * component just renders the label, the pencil toggle, and the "edited" hint.
 * When `lockable` is false it renders as a plain labelled field (free-form entry
 * for a fresh draft).
 */
import { Pencil } from "lucide-react";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface EditableFieldProps {
  label: string;
  required?: boolean;
  /** Per-field-lock mode on (i.e. editing a new version). */
  lockable?: boolean;
  /** This field is currently unlocked for editing. */
  unlocked?: boolean;
  /** Changed from the previous version's value. */
  modified?: boolean;
  onToggle?: () => void;
  helper?: string;
  children: React.ReactNode;
}

export const EditableField = ({
  label,
  required,
  lockable,
  unlocked,
  modified,
  onToggle,
  helper,
  children,
}: EditableFieldProps) => (
  <div className="space-y-1.5">
    <Label className="z-secondary inline-flex items-center gap-1.5">
      <span>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </span>
      {modified && (
        <span className="z-hint font-medium text-warning">· edited</span>
      )}
    </Label>
    {lockable ? (
      <div className="flex items-start gap-1.5">
        <div className="flex-1 min-w-0">{children}</div>
        <button
          type="button"
          onClick={onToggle}
          title={unlocked ? "Lock field" : "Edit field"}
          aria-label={unlocked ? `Lock ${label}` : `Edit ${label}`}
          className={cn(
            "mt-0.5 shrink-0 rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted",
            unlocked && "bg-muted text-foreground"
          )}
        >
          <Pencil className="size-3.5" />
        </button>
      </div>
    ) : (
      children
    )}
    {helper && <p className="z-caption text-muted-foreground">{helper}</p>}
  </div>
);

export default EditableField;
